import React, { useEffect, useState } from 'react';
import { Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { googleAdsAuth } from '../../services/googleAdsAuth';

/**
 * OAuth Callback Handler for Google Ads
 * Handles the redirect from Google OAuth and exchanges code for tokens
 */
const OAuthCallback: React.FC = () => {
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [message, setMessage] = useState('Processing authorization...');
  
  useEffect(() => {
    handleCallback();
  }, []);

  const handleCallback = async () => {
    try {
      // Get authorization code and state from URL
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      const state = urlParams.get('state'); // Contains orgId
      const error = urlParams.get('error');

      if (error) {
        throw new Error(`OAuth error: ${error}`);
      }

      if (!code) {
        throw new Error('No authorization code received');
      }

      if (!state) {
        throw new Error('No state parameter received');
      }

      setMessage('Exchanging authorization code for tokens...');

      // Exchange code for tokens
      const result = await googleAdsAuth.handleOAuthCallback(code, state);

      if (!result.success) {
        throw new Error(result.error || 'Failed to complete authorization');
      }

      setStatus('success');
      setMessage('Google Ads connected successfully!');

      // Redirect back to Google Ads page after 2 seconds
      setTimeout(() => {
        window.location.href = '/#google-ads';
      }, 2000);

    } catch (error: any) {
      console.error('OAuth callback error:', error);
      setStatus('error');
      setMessage(error.message || 'Authorization failed');
    }
  };

  return (
    <div className="min-h-screen bg-bg-base flex items-center justify-center p-4">
      <div className="bg-bg-card rounded-2xl shadow-heavy p-8 max-w-md w-full text-center">
        {status === 'processing' && (
          <>
            <div className="inline-flex items-center justify-center w-16 h-16 bg-info-soft rounded-full mb-4">
              <Loader2 className="w-8 h-8 text-accent animate-spin" />
            </div>
            <h2 className="text-xl font-bold text-txt mb-2">Connecting to Google Ads</h2>
            <p className="text-txt-secondary">{message}</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="inline-flex items-center justify-center w-16 h-16 bg-success-soft rounded-full mb-4">
              <CheckCircle className="w-8 h-8 text-success" />
            </div>
            <h2 className="text-xl font-bold text-txt mb-2">Connected Successfully!</h2>
            <p className="text-txt-secondary mb-4">{message}</p>
            <p className="text-sm text-txt-tertiary">Redirecting you back...</p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="inline-flex items-center justify-center w-16 h-16 bg-danger-soft rounded-full mb-4">
              <AlertCircle className="w-8 h-8 text-danger" />
            </div>
            <h2 className="text-xl font-bold text-txt mb-2">Authorization Failed</h2>
            <p className="text-danger mb-4">{message}</p>
            <button
              onClick={() => window.location.href = '/#google-ads'}
              className="px-6 py-2 bg-primary text-primary-contrast rounded-lg hover:bg-bg-card transition-all"
            >
              Back to Google Ads
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default OAuthCallback;

